import { forwardRef, memo, HTMLAttributes, useMemo } from "react";
import clsx from "classnames";
import { useStyles } from "./HeroBanner.style";
import { Typography } from "../../core/Typography/Typography";
import {
  TypographyColor,
  TypographyVariant,
} from "../../../constants/typography";
import { useQuery } from "@apollo/client";
import { featuredCardsAaaGql, Query } from "@onmo/graphql/graphql";
import { findFeaturedCardData } from "../../../models/console";
import {
  ByogId,
  EssentialPassId,
  FeatureDesktopGroup,
  FeatureMobileGroup,
} from "../../../constants/console";
import { StaticImage } from "../../core/StaticImage/StaticImage";
import { useAppSelector } from "../../hooks";
import { selectIsDesktopView } from "../../../store/slices/appState";
import { useTranslation } from "react-i18next";

interface HeroBannerProps extends HTMLAttributes<HTMLDivElement> {
  type: "essentialPack" | "install";
}

const disclaimerText = {
  brand: "ONMO+",
  dataProvider: "IGDB",
  contentKey:
    "HeroBanner_provides private cloud access for games you already own. We do not sell, distribute, or license game titles. Steam ownership is required. All trademarks belong to their respective owners. Assets and metadata provided via",
};

export const HeroBanner = memo(
  forwardRef<HTMLDivElement, HeroBannerProps>((props, ref) => {
    const { type, className, ...rest } = props;
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const isDesktop = useAppSelector(selectIsDesktopView);
    const { data: featuredCardsData } = useQuery<Query>(featuredCardsAaaGql, {
      variables: {
        input: { group: isDesktop ? FeatureDesktopGroup : FeatureMobileGroup },
      },
    });
    const data = useMemo(
      () =>
        findFeaturedCardData(
          featuredCardsData?.featuredCards?.items,
          type === "essentialPack" ? EssentialPassId : ByogId,
        ),
      [featuredCardsData?.featuredCards?.items, type, i18n.language],
    );
    const assets = data?.assets;

    const disclaimerNode = type === "install" && (
      <Typography
        variant={TypographyVariant.BodyB5Medium}
        color={TypographyColor.Gray}
        className={clsx(classes.disclaimerText, isDesktop && "isDesktop")}
      >
        <strong>{disclaimerText.brand}</strong> {t(disclaimerText.contentKey)}{" "}
        {disclaimerText.dataProvider}.
      </Typography>
    );

    return (
      <div
        ref={ref}
        {...rest}
        className={clsx(classes.root, className)}
        data-gamepad-disabled
      >
        <div className={clsx(classes.content, isDesktop && "isDesktop")}>
          {assets?.image1X && (
            <StaticImage
              className={classes.image}
              src={assets.image1X}
              src2x={assets.image2X}
              src3x={assets.image3X}
            />
          )}
          {isDesktop && disclaimerNode}
        </div>
        {!isDesktop && disclaimerNode}
      </div>
    );
  }),
);

HeroBanner.displayName = "HeroBanner";
